import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar, Copy, AlertCircle, CheckCircle2, Search, ArrowRight, Users } from 'lucide-react';
import { getAllTasksByMonth, duplicateRoutineTasksToMonth, StaffTodo } from '@/services/staffTodoService';
import { getStaff, Staff } from '@/services/adminService';
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import LoadingOverlay from './LoadingOverlay';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface DuplicateRoutineTasksModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  staffId?: string;
  isAdmin?: boolean;
}

const DuplicateRoutineTasksModal: React.FC<DuplicateRoutineTasksModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  staffId,
  isAdmin = false
}) => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1; // 1-12

  const [sourceYear, setSourceYear] = useState<number>(currentMonth === 1 ? currentYear - 1 : currentYear);
  const [sourceMonth, setSourceMonth] = useState<number>(currentMonth === 1 ? 12 : currentMonth - 1);
  const [targetYear, setTargetYear] = useState<number>(currentYear);
  const [targetMonth, setTargetMonth] = useState<number>(currentMonth);

  const [allTasks, setAllTasks] = useState<StaffTodo[]>([]);
  const [selectedTaskIds, setSelectedTaskIds] = useState<Set<string>>(new Set());
  const [isLoadingTasks, setIsLoadingTasks] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<'select' | 'tasks' | 'confirm' | 'success'>('select');

  // Admin staff selection
  const [staffList, setStaffList] = useState<Staff[]>([]);
  const [selectedStaffId, setSelectedStaffId] = useState<string | undefined>(staffId);
  const [isLoadingStaff, setIsLoadingStaff] = useState(false);

  // Generate year options (current year ± 2 years)
  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

  // Month options
  const monthOptions = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  // Reset state and load staff list when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep('select');
      setAllTasks([]);
      setSelectedTaskIds(new Set());
      setError(null);
      setSelectedStaffId(staffId); // Reset to user's own staffId

      // Load staff list for admin
      if (isAdmin) {
        setIsLoadingStaff(true);
        getStaff()
          .then((staff) => {
            // Filter to only active staff
            const activeStaff = staff.filter(s => s.status === 'active');
            setStaffList(activeStaff);
          })
          .catch((err) => {
            console.error('Error loading staff list:', err);
          })
          .finally(() => {
            setIsLoadingStaff(false);
          });
      }
    }
  }, [isOpen, isAdmin, staffId]);

  const loadTasks = useCallback(async () => {
    // For admin without staff selected, show error
    if (isAdmin && !selectedStaffId) {
      setError('Please select a staff member first.');
      return;
    }

    setIsLoadingTasks(true);
    setError(null);

    try {
      const tasks = await getAllTasksByMonth(sourceYear, sourceMonth, selectedStaffId);
      setAllTasks(tasks);

      // Default selection: routine tasks checked, non-routine unchecked
      const defaultSelected = new Set<string>();
      tasks.forEach(task => {
        if (task.task_type === 'Routine') {
          defaultSelected.add(task.id);
        }
      });
      setSelectedTaskIds(defaultSelected);

      setStep('tasks');
    } catch (err) {
      console.error('Error loading tasks:', err);
      setError('Failed to load tasks. Please try again.');
      setAllTasks([]);
    } finally {
      setIsLoadingTasks(false);
    }
  }, [sourceYear, sourceMonth, selectedStaffId, isAdmin]);

  const handleSearchTasks = () => {
    loadTasks();
  };

  const handleToggleTask = (taskId: string) => {
    setSelectedTaskIds(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    setSelectedTaskIds(new Set(allTasks.map(t => t.id)));
  };

  const handleDeselectAll = () => {
    setSelectedTaskIds(new Set());
  };

  const handleProceedToConfirm = () => {
    setStep('confirm');
  };

  const handleBackToTasks = () => {
    setStep('tasks');
  };

  const selectedTasks = allTasks.filter(t => selectedTaskIds.has(t.id));

  const handleDuplicate = async () => {
    if (selectedTasks.length === 0) {
      return;
    }

    setIsDuplicating(true);
    setError(null);

    try {
      await duplicateRoutineTasksToMonth(selectedTasks, targetYear, targetMonth);
      setStep('success');
      setIsDuplicating(false);
      // Wait 2 seconds to show success message, then close and refresh
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 2000);
    } catch (err) {
      console.error('Error duplicating tasks:', err);
      setError('Failed to duplicate tasks. Please try again.');
      setIsDuplicating(false);
    }
  };

  const handleClose = () => {
    if (!isDuplicating && step !== 'success') {
      setError(null);
      setAllTasks([]);
      setSelectedTaskIds(new Set());
      setStep('select');
      onClose();
    }
  };

  const sourceMonthName = monthOptions.find(m => m.value === sourceMonth)?.label;
  const targetMonthName = monthOptions.find(m => m.value === targetMonth)?.label;
  const selectedStaffName = staffList.find(s => s.id === selectedStaffId)?.name;

  const routineCount = allTasks.filter(t => t.task_type === 'Routine').length;
  const adHocCount = allTasks.filter(t => t.task_type !== 'Routine').length;
  const selectedRoutineCount = selectedTasks.filter(t => t.task_type === 'Routine').length;
  const selectedAdHocCount = selectedTasks.filter(t => t.task_type !== 'Routine').length;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
        {(isLoadingTasks || isDuplicating) && (
          <LoadingOverlay
            isLoading={true}
            message={isDuplicating ? "Duplicating tasks..." : "Loading tasks..."}
          />
        )}

        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Copy className="h-5 w-5" />
            Duplicate Tasks - Step by Step
          </DialogTitle>
          <DialogDescription>
            {step === 'select' && "Step 1: Select which month to copy tasks from and to"}
            {step === 'tasks' && "Step 2: Select which tasks to duplicate"}
            {step === 'confirm' && "Step 3: Confirm and execute the duplication"}
            {step === 'success' && "Tasks duplicated successfully!"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
          {/* Step 1: Month Selection */}
          {step === 'select' && (
            <>
              <Alert>
                <AlertDescription className="text-sm">
                  <strong>What will happen:</strong> You'll select a source month, then choose which tasks to duplicate
                  to the target month. Routine tasks will be pre-selected by default.
                </AlertDescription>
              </Alert>

              {/* Staff Selection - Only for Admin */}
              {isAdmin && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Select Staff Member
                  </Label>
                  <Select
                    value={selectedStaffId || ''}
                    onValueChange={(value) => setSelectedStaffId(value)}
                    disabled={isLoadingStaff}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={isLoadingStaff ? "Loading staff..." : "Select a staff member"} />
                    </SelectTrigger>
                    <SelectContent>
                      {staffList.map(staff => (
                        <SelectItem key={staff.id} value={staff.id}>
                          {staff.name} {staff.role ? `(${staff.role})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Select which staff member's tasks you want to duplicate
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Copy FROM (Source Month)
                  </Label>
                  <div className="grid grid-cols-2 gap-2">
                    <Select
                      value={sourceMonth.toString()}
                      onValueChange={(value) => setSourceMonth(parseInt(value))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {monthOptions.map(month => (
                          <SelectItem key={month.value} value={month.value.toString()}>
                            {month.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={sourceYear.toString()}
                      onValueChange={(value) => setSourceYear(parseInt(value))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {yearOptions.map(year => (
                          <SelectItem key={year} value={year.toString()}>
                            {year}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Select the month containing tasks you want to duplicate
                  </p>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium flex items-center gap-2">
                    <ArrowRight className="h-4 w-4 text-blue-600" />
                    Copy TO (Target Month)
                  </Label>
                  <div className="grid grid-cols-2 gap-2">
                    <Select
                      value={targetMonth.toString()}
                      onValueChange={(value) => setTargetMonth(parseInt(value))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {monthOptions.map(month => (
                          <SelectItem key={month.value} value={month.value.toString()}>
                            {month.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={targetYear.toString()}
                      onValueChange={(value) => setTargetYear(parseInt(value))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {yearOptions.map(year => (
                          <SelectItem key={year} value={year.toString()}>
                            {year}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Select the month where duplicate tasks will be created
                  </p>
                </div>
              </div>

              <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
                <h4 className="font-medium text-sm text-blue-900 mb-2">Next Step:</h4>
                <p className="text-sm text-blue-800">
                  Click "Search for Tasks" to find all tasks in <strong>{sourceMonthName} {sourceYear}</strong>.
                  You'll then be able to select which tasks to duplicate.
                </p>
              </div>
            </>
          )}

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Step 2: Task Selection */}
          {step === 'tasks' && (
            <>
              <Alert>
                <AlertDescription className="text-sm">
                  <strong>Found:</strong> {allTasks.length} {allTasks.length === 1 ? 'task' : 'tasks'}
                  {isAdmin && selectedStaffName && <> for <strong>{selectedStaffName}</strong></>} in{' '}
                  <strong>{sourceMonthName} {sourceYear}</strong>.
                  {' '}Select the tasks you want to duplicate.
                  {routineCount > 0 && <> Routine tasks ({routineCount}) are pre-selected.</>}
                </AlertDescription>
              </Alert>

              <div className="border rounded-md overflow-hidden flex-1 flex flex-col">
                <div className="p-3 bg-gray-50 border-b flex items-center justify-between">
                  <h3 className="text-sm font-medium">
                    Tasks from {sourceMonthName} {sourceYear}
                    <span className="ml-2 text-muted-foreground font-normal">
                      ({selectedTaskIds.size} of {allTasks.length} selected)
                    </span>
                  </h3>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={handleSelectAll} className="text-xs h-7">
                      Select All
                    </Button>
                    <Button variant="ghost" size="sm" onClick={handleDeselectAll} className="text-xs h-7">
                      Deselect All
                    </Button>
                  </div>
                </div>

                <div className="p-3 overflow-y-auto" style={{ maxHeight: '45vh' }}>
                  {allTasks.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No tasks found in this month</p>
                      <p className="text-xs mt-1">Try a different source month</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {allTasks.map((task) => {
                        const isSelected = selectedTaskIds.has(task.id);
                        const isRoutine = task.task_type === 'Routine';
                        return (
                          <div
                            key={task.id}
                            className={`p-3 border rounded-md cursor-pointer transition-colors ${
                              isSelected
                                ? 'bg-blue-50 border-blue-200'
                                : 'bg-white border-gray-200 hover:bg-gray-50'
                            }`}
                            onClick={() => handleToggleTask(task.id)}
                          >
                            <div className="flex items-start gap-3">
                              <div className="flex-shrink-0 pt-0.5">
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={() => handleToggleTask(task.id)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="text-sm font-medium mb-1">{task.title}</h4>
                                {task.description && (
                                  <div
                                    className="text-xs text-gray-600 mb-2 prose prose-sm max-w-none [&>p]:my-1 [&>ul]:my-1 [&>ol]:my-1 [&>ul]:pl-4 [&>ol]:pl-4 [&_li]:my-0 line-clamp-2"
                                    dangerouslySetInnerHTML={{ __html: task.description }}
                                  />
                                )}
                                <div className="flex flex-wrap items-center gap-2">
                                  <Badge
                                    variant="outline"
                                    className={`text-xs ${
                                      isRoutine
                                        ? 'bg-blue-50 text-blue-700 border-blue-200'
                                        : 'bg-orange-50 text-orange-700 border-orange-200'
                                    }`}
                                  >
                                    {task.task_type || 'Ad-hoc'}
                                  </Badge>
                                  {task.category && (
                                    <Badge variant="outline" className="text-xs">
                                      {task.category}
                                    </Badge>
                                  )}
                                  <span className="text-xs text-gray-500">
                                    Due: {format(new Date(task.deadline), 'MMM d, yyyy')}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Step 3: Confirmation */}
          {step === 'confirm' && (
            <>
              <Alert className="bg-yellow-50 border-yellow-200">
                <AlertCircle className="h-4 w-4 text-yellow-600" />
                <AlertDescription className="text-sm text-yellow-800">
                  <strong>Ready to duplicate!</strong> Review the details below and click "Execute Duplication" when ready.
                </AlertDescription>
              </Alert>

              <div className="space-y-3">
                <div className="p-4 border rounded-md bg-white">
                  <h4 className="font-medium text-sm mb-3">Duplication Summary:</h4>
                  <div className="space-y-2 text-sm">
                    {isAdmin && selectedStaffName && (
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground">Staff:</span>
                        <strong>{selectedStaffName}</strong>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground">Source:</span>
                      <strong>{sourceMonthName} {sourceYear}</strong>
                    </div>
                    <div className="flex items-center gap-2">
                      <ArrowRight className="h-4 w-4 text-blue-600" />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground">Target:</span>
                      <strong>{targetMonthName} {targetYear}</strong>
                    </div>
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t">
                      <span className="text-muted-foreground">Tasks to duplicate:</span>
                      <Badge>{selectedTasks.length} {selectedTasks.length === 1 ? 'task' : 'tasks'}</Badge>
                      {selectedRoutineCount > 0 && (
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-xs">
                          {selectedRoutineCount} Routine
                        </Badge>
                      )}
                      {selectedAdHocCount > 0 && (
                        <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200 text-xs">
                          {selectedAdHocCount} Ad-hoc
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>

                <div className="p-4 border rounded-md bg-green-50 border-green-200">
                  <h4 className="font-medium text-sm text-green-900 mb-2">What will happen:</h4>
                  <ul className="space-y-1 text-sm text-green-800">
                    <li>- {selectedTasks.length} new {selectedTasks.length === 1 ? 'task' : 'tasks'} will be created in {targetMonthName} {targetYear}</li>
                    <li>- Due dates will be adjusted to match the target month (same day of month)</li>
                    <li>- Task status will be reset to "To-Do"</li>
                    <li>- Original tasks remain unchanged</li>
                    <li>- Completion status and ratings will be cleared for new tasks</li>
                  </ul>
                </div>
              </div>
            </>
          )}

          {/* Step 4: Success */}
          {step === 'success' && (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center space-y-4">
                <CheckCircle2 className="h-16 w-16 text-green-600 mx-auto" />
                <div>
                  <h3 className="text-lg font-medium text-green-900 mb-1">
                    Successfully Duplicated!
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {selectedTasks.length} {selectedTasks.length === 1 ? 'task has' : 'tasks have'} been created in {targetMonthName} {targetYear}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          {/* Step 1: Select */}
          {step === 'select' && (
            <>
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button onClick={handleSearchTasks} disabled={isLoadingTasks}>
                <Search className="h-4 w-4 mr-2" />
                Search for Tasks
              </Button>
            </>
          )}

          {/* Step 2: Task Selection */}
          {step === 'tasks' && (
            <>
              <Button variant="outline" onClick={() => setStep('select')}>
                Back to Selection
              </Button>
              <Button
                onClick={handleProceedToConfirm}
                disabled={selectedTaskIds.size === 0}
              >
                <ArrowRight className="h-4 w-4 mr-2" />
                Continue ({selectedTaskIds.size} selected)
              </Button>
            </>
          )}

          {/* Step 3: Confirm */}
          {step === 'confirm' && (
            <>
              <Button variant="outline" onClick={handleBackToTasks}>
                Back to Task Selection
              </Button>
              <Button
                onClick={handleDuplicate}
                disabled={isDuplicating}
                className="bg-green-600 hover:bg-green-700"
              >
                <Copy className="h-4 w-4 mr-2" />
                Execute Duplication
              </Button>
            </>
          )}

          {/* Step 4: Success */}
          {step === 'success' && (
            <Button onClick={handleClose} className="w-full">
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Close
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DuplicateRoutineTasksModal;
